import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, signal } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { AssetCategoryViewModel } from "src/app/core/model/ContentManagement";
import { DemoAssetService } from "src/app/services/demo-asset.service";
import { ValueChangedEvent } from "devextreme/ui/select_box";
import { confirm } from "devextreme/ui/dialog";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "brd-asset-category-editor",
  templateUrl: "./asset-category-editor.component.html",
  styleUrl: "./asset-category-editor.component.less",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AssetCategoryEditorComponent,
      multi: true
    }
  ],
  standalone: false
})
export class AssetCategoryEditorComponent implements ControlValueAccessor {
  destroyRef = inject(DestroyRef);
  @Input() categories: AssetCategoryViewModel[] = [];
  @Input() validationStatus: "valid" | "invalid" = "valid";
  @Input() validationErrors: { type: string; message: string }[] = [];
  selectedCategory = 0;
  onChange!: (arg0: number) => void;
  onTouched!: () => void;
  disabled = false;

  popupEditorVisible = signal(false);
  popupTitle = "Create new category";
  categoryName = "";
  categoryColor = "#000000";
  categoryIcon = "";
  categoryId = 0;

  cancelButtonOptions = {
    text: "Cancel",
    type: "normal",
    stylingMode: "outline",
    onClick: (): void => {
      this.popupEditorVisible.set(false);
    }
  };

  saveButtonOptions = {
    text: "Save",
    stylingMode: "contained",
    onClick: (): void => {
      this.saveCategory();
    }
  };

  constructor(private readonly demoAssetService: DemoAssetService) {}

  writeValue(obj: number): void {
    this.selectedCategory = obj;
  }
  registerOnChange(fn: (arg0: number) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onCategorySelectionChanged(e: ValueChangedEvent): void {
    this.updateSelctedCategory(e.value);
  }

  iconClick(e: Event, action: "ADD" | "EDIT" | "DELETE", category?: AssetCategoryViewModel): void {
    e.stopPropagation();

    switch (action) {
      case "ADD":
        this.popupEditorVisible.set(true);
        this.popupTitle = "Create new category";
        this.categoryName = "";
        this.categoryColor = "#000000";
        this.categoryIcon = "";
        this.categoryId = 0;
        break;
      case "EDIT":
        this.popupEditorVisible.set(true);
        this.popupTitle = `Edit category ${category.name}`;
        this.categoryName = category.name;
        this.categoryColor = category.color;
        this.categoryIcon = category.icon;
        this.categoryId = category.id;
        break;
      case "DELETE":
        confirm(`Are you sure to delete <strong>${category.name}</strong>?`, "Delete category").then(dialogResult => {
          if (dialogResult) {
            this.demoAssetService
              .deleteCategory(category.id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(res => {
                this.categories = this.categories.filter(x => x.id !== res.id);
                if (this.selectedCategory === res.id) {
                  this.updateSelctedCategory(0);
                }
              });
          }
        });
        break;
    }
  }

  saveCategory(): void {
    if (!this.categoryName || !this.categoryColor) return;

    const newCategory = {
      id: this.categoryId,
      name: this.categoryName,
      color: this.categoryColor,
      icon: this.categoryIcon
    };
    if (this.categoryId) {
      this.demoAssetService
        .updateCategory(this.categoryId, newCategory)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(res => {
          const index = this.categories.findIndex(x => x.id === res.id);
          this.categories[index] = res;
          this.updateSelctedCategory(res.id);
          this.popupEditorVisible.set(false);
        });
    } else {
      this.demoAssetService
        .addCategory(newCategory)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(res => {
          this.categories.push(res);
          this.updateSelctedCategory(res.id);
          this.popupEditorVisible.set(false);
        });
    }
  }

  private updateSelctedCategory(selectedCategory: number): void {
    this.selectedCategory = selectedCategory;
    this.onChange(selectedCategory);
    this.onTouched();
  }
}
